import { useEffect, useRef, useState } from 'react'
import { snackBarVisibleVar, useAuth } from 'context'
import { FormProvider, useForm } from 'react-hook-form'
import {
  EhrCoachRegistrationStatus,
  IUserFragment,
  useSubmitCoachProfileMutation,
  useUpsertCoachProfileMutation
} from 'types'
import { buildUpsertVariables } from './buildUpsertVariables'
import { ErrorSummary } from './components/ErrorSummary'
import { OnboardingHero } from './components/OnboardingHero'
import { OnboardingTopBar } from './components/OnboardingTopBar'
import { SectionProgress } from './components/SectionProgress'
import { ONBOARDING_SECTIONS, OnboardingSectionId } from './constants'
import { mapMissingFieldsToErrors } from './mapMissingFieldsToErrors'
import { mapProfileToDefaults } from './mapProfileToDefaults'
import {
  ActionBarInnerStyled,
  ActionBarStyled,
  DraftButtonStyled,
  FormFieldsetStyled,
  OnboardingRootStyled,
  PageFootStyled,
  PageStyled,
  PendingNoteStyled,
  SubmittedStatusStyled,
  SubmitButtonStyled
} from './onboardingStyles'
import { AboutSection } from './sections/AboutSection'
import { CredentialsSection } from './sections/CredentialsSection'
import { PersonalSection } from './sections/PersonalSection'
import { SpecialtiesSection } from './sections/SpecialtiesSection'
import { StateLicensesSection } from './sections/StateLicensesSection'
import { ICoachProfileFormValues } from './types'

interface IProfileForm {
  currentUser: IUserFragment
}

export const ProfileForm = ({ currentUser }: IProfileForm) => {
  const { logout } = useAuth()
  const submitted =
    currentUser.coachProfile?.registrationStatus === EhrCoachRegistrationStatus.PendingApproval
  const methods = useForm<ICoachProfileFormValues>({
    mode: 'onTouched',
    disabled: submitted,
    defaultValues: mapProfileToDefaults(currentUser)
  })
  const {
    handleSubmit,
    getValues,
    setError,
    formState: { errors }
  } = methods

  const [saveStatus, setSaveStatus] = useState<'saved' | 'unsaved'>('saved')
  const [showSummary, setShowSummary] = useState(false)
  const [activeSection, setActiveSection] = useState<OnboardingSectionId>('personal')

  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined
    // Track which sections are currently in the active band rather than picking whichever
    // entry happens to fire last: as the user scrolls, two adjacent sections can intersect
    // simultaneously (especially short ones like an empty Licenses), and the "last entry
    // wins" approach would skip a section. Instead we recompute and choose the topmost
    // section still inside the band.
    const intersecting = new Set<OnboardingSectionId>()
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id.replace('section-', '') as OnboardingSectionId
          if (entry.isIntersecting) {
            intersecting.add(id)
          } else {
            intersecting.delete(id)
          }
        })
        const first = ONBOARDING_SECTIONS.find((id) => intersecting.has(id))
        if (first) setActiveSection(first)
      },
      { root: rootRef.current, rootMargin: '-40% 0px -50% 0px' }
    )
    ONBOARDING_SECTIONS.forEach((id) => {
      const el = document.getElementById(`section-${id}`)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [])

  const markUnsaved = () => setSaveStatus('unsaved')

  const onError = (error: { message: string }) =>
    snackBarVisibleVar({ open: true, severity: 'error', message: error.message })

  const [upsertCoachProfile, { loading: saving }] = useUpsertCoachProfileMutation({
    onError,
    refetchQueries: ['currentUser']
  })
  const [submitCoachProfile, { loading: submitting }] = useSubmitCoachProfileMutation({
    onError,
    refetchQueries: ['currentUser']
  })

  const existingPhotoUrl = currentUser.coachProfile?.photo
  const existingCardUrl = currentUser.coachProfile?.cdrCnsCard
  const pendingRegisterNote = currentUser.coachProfile?.pendingRegisterNote

  // Persists name and profile (name travels through upsertCoachProfile, which a coach is
  // authorized for), returning false on error so callers can stop (e.g. not submit
  // half-saved data). Errors surface via the shared onError handler.
  const persist = async (values: ICoachProfileFormValues): Promise<boolean> => {
    try {
      const { errors: upsertErrors } = await upsertCoachProfile({
        variables: buildUpsertVariables(values)
      })
      return !upsertErrors?.length
    } catch {
      return false
    }
  }

  const handleSaveDraft = async () => {
    if (await persist(getValues())) {
      setSaveStatus('saved')
      snackBarVisibleVar({ open: true, severity: 'success', message: 'Draft saved' })
    }
  }

  // Scrolls the first errored field in DOM order into view and focuses it so the coach
  // sees concrete feedback after hitting Submit — otherwise a far-down missing field
  // (e.g. headshot) leaves them looking at the action bar with nothing visibly wrong.
  const focusFirstInvalid = () => {
    requestAnimationFrame(() => {
      const target = document.querySelector<HTMLElement>('[aria-invalid="true"]')
      if (!target) return
      target.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
      target.focus?.({ preventScroll: true })
    })
  }

  const handleSubmitForApproval = handleSubmit(
    async (values) => {
      setShowSummary(false)
      if (!(await persist(values))) return
      setSaveStatus('saved')

      const { data } = await submitCoachProfile()
      const result = data?.submitCoachProfile
      if (!result || result.success) return

      mapMissingFieldsToErrors(result.missingFields).forEach(({ name, message }) =>
        setError(name, { message })
      )
      setShowSummary(true)
      focusFirstInvalid()
      // On success the currentUser refetch flips registrationStatus to pending_approval,
      // and the registration gate redirects to the pending-approval screen.
    },
    () => {
      setShowSummary(true)
      focusFirstInvalid()
    }
  )

  const initials = `${currentUser.firstName?.[0] ?? ''}${
    currentUser.lastName?.[0] ?? ''
  }`.toUpperCase()

  return (
    <OnboardingRootStyled ref={rootRef} onInput={markUnsaved} onChange={markUnsaved}>
      <OnboardingTopBar email={currentUser.email} initials={initials} onSignOut={() => logout()} />

      <PageStyled>
        <OnboardingHero />
        <SectionProgress activeSection={activeSection} />

        {pendingRegisterNote && !submitted && (
          <PendingNoteStyled>
            <div className="title">Your manager asked for changes</div>
            <div className="body">{pendingRegisterNote}</div>
          </PendingNoteStyled>
        )}

        {!submitted && <ErrorSummary errors={errors} show={showSummary} />}

        <FormProvider {...methods}>
          <form onSubmit={handleSubmitForApproval} noValidate>
            {/* fieldset disabled cleanly freezes every native control + button inside while
                still rendering the values, which is exactly what the submitted view needs. */}
            <FormFieldsetStyled disabled={submitted}>
              <PersonalSection />
              <CredentialsSection
                existingCardUrl={existingCardUrl}
                existingPhotoUrl={existingPhotoUrl}
              />
              <StateLicensesSection />
              <SpecialtiesSection onChange={markUnsaved} />
              <AboutSection />
            </FormFieldsetStyled>

            <PageFootStyled>
              Your responses are private. After you submit, our care team reviews within 1–2
              business days.
            </PageFootStyled>
          </form>
        </FormProvider>
      </PageStyled>

      <ActionBarStyled role="region" aria-label="Form actions">
        <ActionBarInnerStyled>
          {submitted ? (
            <SubmittedStatusStyled>
              <span className="dot" aria-hidden="true" />
              <span>
                <strong>Submitted</strong> · awaiting approval
              </span>
            </SubmittedStatusStyled>
          ) : (
            <>
              <div className="save-meta">
                <span className="dot" aria-hidden="true" />
                <span>{saveStatus === 'saved' ? 'Draft saved' : 'Unsaved changes'}</span>
              </div>
              <div className="actions">
                <DraftButtonStyled
                  variant="outlined"
                  color="inherit"
                  loading={saving}
                  onClick={handleSaveDraft}
                >
                  Save draft
                </DraftButtonStyled>
                <SubmitButtonStyled
                  variant="contained"
                  color="success"
                  disableElevation
                  loading={saving || submitting}
                  onClick={handleSubmitForApproval}
                >
                  Submit for approval
                </SubmitButtonStyled>
              </div>
            </>
          )}
        </ActionBarInnerStyled>
      </ActionBarStyled>
    </OnboardingRootStyled>
  )
}
